import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, createCommentVNode as _createCommentVNode, vModelText as _vModelText, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "keyword-input-container" }
const _hoisted_2 = { class: "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus-within:ring-2 focus-within:ring-primary focus-within:border-primary transition duration-200 min-h-[80px]" }
const _hoisted_3 = {
  key: 0,
  class: "flex flex-wrap gap-2 mb-2"
}
const _hoisted_4 = ["onClick"]
const _hoisted_5 = ["placeholder"]

import { ref, computed } from 'vue'
import { X } from 'lucide-vue-next'

interface Props {
  modelValue: string[]
  placeholder?: string
}

interface Emits {
  (e: 'update:modelValue', value: string[]): void
}

// Props and emits

export default /*@__PURE__*/_defineComponent({
  __name: 'KeywordInput',
  props: {
    modelValue: {},
    placeholder: { default: 'Enter keywords or key points...' }
  },
  emits: ["update:modelValue"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props

const emit = __emit

// Local state
const currentInput = ref('')
const inputRef = ref<HTMLInputElement>()

// Use a computed property to get keywords directly from props
const keywords = computed({
  get: () => props.modelValue || [],
  set: (value: string[]) => emit('update:modelValue', value)
})

// Handle keyboard input
const handleKeydown = (event: KeyboardEvent) => {
  // Handle space, enter, comma, or semicolon as delimiters
  if (event.key === ' ' || event.key === 'Enter' || event.key === ',' || event.key === ';') {
    event.preventDefault()
    addKeyword()
  }
  
  // Handle backspace when input is empty to remove last keyword
  if (event.key === 'Backspace' && currentInput.value === '' && keywords.value.length > 0) {
    event.preventDefault()
    removeKeyword(keywords.value.length - 1)
  }
}

const handleKeyup = (event: KeyboardEvent) => {
  // Also handle space on keyup to ensure it's captured
  if (event.key === ' ') {
    addKeyword()
  }
}

// Add a new keyword
const addKeyword = () => {
  const trimmedInput = currentInput.value.trim()
  
  if (trimmedInput === '') {
    return
  }
  
  // Split by common delimiters and add each part as a keyword
  const newKeywords = trimmedInput
    .split(/[,;]/)
    .map(keyword => keyword.trim())
    .filter(keyword => keyword !== '' && !keywords.value.includes(keyword))
  
  if (newKeywords.length > 0) {
    keywords.value = [...keywords.value, ...newKeywords]
    currentInput.value = ''
  }
}

// Remove a keyword by index
const removeKeyword = (index: number) => {
  if (index >= 0 && index < keywords.value.length) {
    const newKeywords = [...keywords.value]
    newKeywords.splice(index, 1)
    keywords.value = newKeywords
  }
}

// Focus the input when the component is clicked
const focusInput = () => {
  inputRef.value?.focus()
}

// Expose methods for parent components
__expose({
  focus: focusInput,
  addKeyword,
  removeKeyword
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      (keywords.value.length > 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(keywords.value, (keyword, index) => {
              return (_openBlock(), _createElementBlock("span", {
                key: index,
                class: "inline-flex items-center px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 text-sm rounded-md"
              }, [
                _createTextVNode(_toDisplayString(keyword) + " ", 1),
                _createElementVNode("button", {
                  onClick: ($event: any) => (removeKeyword(index)),
                  class: "ml-1 text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-100 focus:outline-none",
                  type: "button"
                }, [
                  _createVNode(_unref(X), { class: "w-3 h-3" })
                ], 8, _hoisted_4)
              ]))
            }), 128))
          ]))
        : _createCommentVNode("", true),
      _withDirectives(_createElementVNode("input", {
        ref_key: "inputRef",
        ref: inputRef,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((currentInput).value = $event)),
        onKeydown: handleKeydown,
        onKeyup: handleKeyup,
        type: "text",
        class: "w-full bg-transparent border-none outline-none focus:ring-0 p-0",
        placeholder: keywords.value.length === 0 ? _ctx.placeholder : 'Add more keywords...'
      }, null, 40, _hoisted_5), [
        [_vModelText, currentInput.value]
      ])
    ]),
    _cache[1] || (_cache[1] = _createElementVNode("p", { class: "text-xs text-secondary-text dark:text-secondary-dark-text mt-1" }, " Type keywords and press space or enter to add them as tags ", -1))
  ]))
}
}

})